<template>
  <div>
    <el-dialog
      :visible.sync="isVisible"
      :before-close="close"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :append-to-body="true"
      custom-class="indication-overview-dialog"
      top="24px"
      width="calc(100% - 48px)"
    >
      <div class="indication-overview" v-if="consultation">
        <div class="overview-body">
          <aside class="category-menu">
            <button
              v-for="category in categories"
              :key="category.id"
              class="category-item"
              :class="{ active: selectedCategory === category.id }"
              @click="selectCategory(category.id)"
            >
              <i :class="category.icon" />
              <span>{{ category.name }}</span>
              <b>{{ categoryCount(category.id) }}</b>
            </button>
          </aside>

          <section class="order-panel">
            <div class="panel-toolbar">
              <div>
                <p class="eyebrow">{{ currentCategory.name }}</p>
                <h3>{{ selectedCategory === 0 ? "Tất cả chỉ định" : "Danh sách phiếu chỉ định" }}</h3>
              </div>
              <div class="view-switch">
                <button :class="{ active: activeView === 'order' }" @click="activeView = 'order'">Phiếu chỉ định
                </button>
                <button :class="{ active: activeView === 'result' }" @click="activeView = 'result'">Kết quả</button>
              </div>
            </div>

            <div class="order-content">
              <div v-if="selectedCategory === 0" class="merged-orders">
                <button
                  v-for="item in mergedIndications"
                  :key="item.key"
                  class="merged-order-item"
                  @click="selectMergedIndication(item)"
                >
                  <span class="order-dot" />
                  <span>{{ item.name }}</span>
                  <small>{{ categoryName(item.cate) }}</small>
                </button>
                <el-empty v-if="!mergedIndications.length" description="Chưa có chỉ định" :image-size="80" />
              </div>

              <div v-else class="order-list">
                <article
                  v-for="order in filteredOrders"
                  :key="order.id"
                  :ref="`order-card-${order.id}`"
                  class="order-card"
                  :class="{ selected: selectedOrder && selectedOrder.id === order.id }"
                  @click="selectOrder(order)"
                >
                  <div class="order-card__top">
                    <span class="order-icon"><i :class="currentCategory.icon" /></span>
                    <div>
                      <h4>{{ order.name || `Phiếu chỉ định #${order.id}` }}</h4>
                      <p>{{ formatDate(order.created_at || order.updated_at) }}</p>
                    </div>
                    <span class="status-badge" :class="orderStatus(order).class">{{ orderStatus(order).text }}</span>
                  </div>
                  <div class="order-card__footer">
                    <span>{{ order.indication ? order.indication.length : 0 }} chỉ định</span>
                    <span>Người chỉ định: {{ order.user_name || order.created_by_name || "—" }}</span>
                  </div>
                </article>
                <el-empty v-if="!filteredOrders.length" description="Chưa có phiếu chỉ định" :image-size="80" />
              </div>
            </div>
          </section>

          <section class="preview-panel">
            <div class="preview-toolbar">
              <div class="preview-toolbar__left">
                <el-button size="mini" plain type="primary" icon="el-icon-plus" @click="addIndication">Thêm chỉ định
                </el-button>
                <el-button
                  v-if="activeView === 'result' && isLaboratory"
                  size="mini"
                  :type="isEditingLaboratoryResults ? 'primary' : 'default'"
                  :icon="isEditingLaboratoryResults ? 'el-icon-check' : 'el-icon-edit'"
                  :loading="savingLaboratoryResults"
                  :disabled="!hasEditableLaboratoryResults"
                  @click="isEditingLaboratoryResults ? confirmSaveLaboratoryResults() : startEditingLaboratoryResults()"
                >{{ isEditingLaboratoryResults ? "Cập nhật kết quả" : "Chỉnh sửa KQ" }}
                </el-button>
                <el-button v-if="isEditingLaboratoryResults" size="mini" :disabled="savingLaboratoryResults"
                           @click="confirmCancelEditingLaboratoryResults">Hủy
                </el-button>
                <el-button v-if="activeView === 'result' && isLaboratory && !isEditingLaboratoryResults" size="mini"
                           icon="el-icon-time" @click="openLaboratoryResultHistory">Lịch sử thay đổi
                </el-button>
              </div>
              <div class="preview-toolbar__right">
                <div v-if="activeView === 'order' && selectedCategory !== 0" class="result-scope">
                  <button :class="{ active: orderScope === 'single' }" @click="orderScope = 'single'">In từng phiếu
                  </button>
                  <button :class="{ active: orderScope === 'merged' }" @click="orderScope = 'merged'">In gộp toàn bộ
                    phiếu
                  </button>
                </div>
                <div v-if="activeView === 'result' && isLaboratory" class="result-scope">
                  <button :class="{ active: resultScope === 'single' }" @click="resultScope = 'single'">Từng phiếu
                  </button>
                  <button :class="{ active: resultScope === 'merged' }" @click="resultScope = 'merged'">Xem gộp</button>
                </div>
                <el-button v-if="printableItems.length" size="mini" type="primary" icon="el-icon-printer"
                           :loading="rendering" @click="handlePrintDocument">{{ printLabel }}
                </el-button>
              </div>
            </div>

            <div class="preview-content">
              <transition name="detail-slide" mode="out-in">
                <div v-if="activeView === 'order'" key="order" class="indication-content-list">
                  <div v-for="(indication, index) in orderDocumentItems" :key="indication.key || indication.id"
                       class="indication-content-item" @click="openIndicationResult(indication)">
                    <span>{{ index + 1 }}</span>
                    <div>
                      <strong>{{ indication.name }}</strong>
                      <small v-if="selectedCategory !== 0">Số lượng: {{ indication.qty || 1 }}</small>
                    </div>
                  </div>
                  <el-empty v-if="!orderDocumentItems.length" description="Chọn một phiếu để xem thông tin"
                            :image-size="80" />
                </div>

                <div v-else-if="isLaboratory" key="laboratory-result" class="result-content">
                  <div v-if="latestManualChange" class="result-last-change"><i class="el-icon-edit-outline" /> Chỉnh sửa
                    cuối: <strong>{{ latestManualChange.actor && latestManualChange.actor.name || "—" }}</strong> ·
                    {{ formatDate(latestManualChange.changed_at) }}
                  </div>
                  <div class="result-table" v-loading="loadingResults">
                    <div class="result-row result-head text-center uppercase">
                      <span>Xét nghiệm</span>
                      <span>Kết quả</span>
                      <span>Đơn vị</span>
                      <span>Bình thường</span>
                      <span>Máy XN</span>
                      <span>Ghi chú</span>
                    </div>
                    <div v-for="item in resultItems" :key="item.key || item.id" class="result-row"
                         :class="{ 'result-group-row': item.isGroup }">
                      <template v-if="item.isGroup">
                        <strong>{{ item.name }}</strong>
                      </template>
                      <template v-else>
                        <span>{{ item.name }}</span>
                        <el-input
                          v-if="isEditingLaboratoryResults"
                          size="mini"
                          :value="editResultValue(item)"
                          :class="resultInputClass(item, 'result')"
                          aria-label="Kết quả xét nghiệm"
                          @focus="setFocusedResultDraft(item, 'result')"
                          @blur="clearFocusedResultDraft"
                          @input="updateResultDraft(item, 'result', $event)"
                        />
                        <strong v-else class="text-center"
                                :class="{ 'result-alert': item.isAlert, 'danger-h': item.typeAlert === 'H', 'danger-l': item.typeAlert === 'L' }">{{ `${resultValue(item)} ${item.typeAlert ? `(${item.typeAlert})` : ""}`
                          }}</strong>
                        <span class="text-center">{{ resultUnit(item) }}</span>
                        <el-input
                          v-if="isEditingLaboratoryResults"
                          size="mini"
                          :value="editResultNormalRate(item)"
                          :class="resultInputClass(item, 'normalRate')"
                          aria-label="Khoảng tham chiếu bình thường"
                          @focus="setFocusedResultDraft(item, 'normalRate')"
                          @blur="clearFocusedResultDraft"
                          @input="updateResultDraft(item, 'normalRate', $event)"
                        />
                        <span v-else class="text-center">{{ resultNormalRate(item) }}</span>
                        <span class="text-center">{{ resultDevice(item) }}</span>
                        <small>{{ item.note || (item.examination_result && item.examination_result.note) || "—"
                          }}</small>
                      </template>
                    </div>
                    <el-empty v-if="!resultItems.length" description="Chưa có kết quả xét nghiệm" :image-size="80" />
                  </div>
                  <div v-if="isEditingLaboratoryResults" class="result-edit-reason">
                    <label for="laboratory-result-edit-reason">Lý do chỉnh sửa</label>
                    <el-input id="laboratory-result-edit-reason" :value="editLaboratoryResultReason" type="textarea"
                              :rows="2" maxlength="500" show-word-limit
                              placeholder="Nhập lý do cập nhật kết quả (nếu có)"
                              @input="updateLaboratoryResultReason" />
                  </div>
                </div>

                <div v-else key="detail" class="result-detail" v-loading="loadingIndicationDetail">
                  <template v-if="selectedIndication">
                    <button class="back-button" @click="selectedIndication = null"><i class="el-icon-arrow-left" /> Danh
                      sách chỉ định
                    </button>
                    <p class="eyebrow">{{ currentCategory.name }}</p>
                    <h2>{{ selectedIndication.name }}</h2>
                    <dl>
                      <div>
                        <dt>Trạng thái</dt>
                        <dd>{{ indicationStatus(selectedIndication).text }}</dd>
                      </div>
                      <div>
                        <dt>Nơi thực hiện</dt>
                        <dd>{{ selectedIndication.target_name || "Chưa cập nhật" }}</dd>
                      </div>
                      <div>
                        <dt>Ngày chỉ định</dt>
                        <dd>{{ formatDate(selectedIndication.created_at || (selectedOrder && selectedOrder.created_at))
                          }}
                        </dd>
                      </div>
                    </dl>
                    <div v-if="indicationResultDescription(selectedIndication)" class="detail-note"><span
                      class="text-uppercase font-bold">Mô tả</span>
                      <div v-html="indicationResultDescription(selectedIndication)" />
                    </div>
                    <div v-if="indicationResultValue(selectedIndication)" class="detail-note"><span
                      class="text-uppercase font-bold">Kết quả chẩn đoán</span>
                      <p>{{ indicationResultValue(selectedIndication) }}</p></div>
                    <div v-if="indicationResultConclusion(selectedIndication)" class="detail-note"><span
                      class="text-uppercase font-bold">Kết luận</span>
                      <div v-html="indicationResultConclusion(selectedIndication)" />
                    </div>
                    <div v-if="indicationResultIdentify(selectedIndication)" class="detail-note"><span
                      class="text-uppercase font-bold">Nhận định kết quả</span>
                      <div v-html="indicationResultIdentify(selectedIndication)" />
                    </div>
                    <div v-if="indicationFiles(selectedIndication).length" class="detail-files">
                      <span class="text-uppercase font-bold">Tệp kết quả</span>
                      <a v-for="file in indicationFiles(selectedIndication)" :key="file.id" :href="file.file_name"
                         target="_blank" rel="noopener">{{ file.name || file.file_name }}</a>
                    </div>
                    <div v-if="!hasResultContent(selectedIndication)" class="detail-note"><span
                      class="text-uppercase font-bold">Kết quả / nhận xét</span>
                      <p>Chưa có kết quả chi tiết.</p></div>
                  </template>
                  <div v-else class="result-list">
                    <p class="eyebrow">{{ currentCategory.name }}</p>
                    <h2>Chọn chỉ định để xem kết quả</h2>
                    <button v-for="item in detailItems" :key="item.key || item.id" @click="openIndicationResult(item)">
                      <span>{{ item.name }}</span><span><em
                      :class="indicationStatus(item).class">{{ indicationStatus(item).text }}</em><i
                      class="el-icon-arrow-right" /></span>
                    </button>
                    <el-empty v-if="!detailItems.length" description="Chưa có chỉ định" :image-size="70" />
                  </div>
                </div>
              </transition>
              <transition name="history-drawer">
                <aside v-if="historyDrawerVisible" class="result-history-drawer">
                  <div class="result-history-drawer__header">
                    <div><p class="eyebrow">Kết quả xét nghiệm</p>
                      <h3>Lịch sử thay đổi</h3></div>
                    <button @click="closeLaboratoryResultHistory"><i class="el-icon-close" /></button>
                  </div>
                  <el-select v-if="laboratoryIndicationOrderIds.length > 1" v-model="historyIndicationOrderId" size="small"
                    class="result-history-select" @change="loadLaboratoryResultHistory">
                    <el-option v-for="id in laboratoryIndicationOrderIds" :key="id" :label="`Phiếu chỉ định #${id}`"
                               :value="id" />
                  </el-select>
                  <div v-loading="loadingResultHistory" class="result-history-list">
                    <button v-for="entry in laboratoryResultHistory" :key="entry.change_set_id"
                            :class="{ active: selectedHistoryChangeSetId === entry.change_set_id }"
                            @click="selectLaboratoryResultHistory(entry)">
                      <strong>{{ entry.actor && entry.actor.name || "—" }}</strong><span>{{ formatDate(entry.changed_at)
                      }} · {{ entry.changed_item_count || 0 }} chỉ số</span><small v-if="entry.reason">{{ entry.reason
                      }}</small></button>
                    <el-empty v-if="!loadingResultHistory && !laboratoryResultHistory.length"
                              description="Chưa có lịch sử chỉnh sửa" :image-size="64" />
                  </div>
                  <div v-if="selectedLaboratoryResultHistory" v-loading="loadingResultHistoryDetail"
                       class="result-history-detail"><h4>Chi tiết thay đổi</h4>
                    <p>
                      <strong>{{ selectedLaboratoryResultHistory.actor && selectedLaboratoryResultHistory.actor.name || "—"
                        }}</strong> · {{ formatDate(selectedLaboratoryResultHistory.changed_at) }}</p>
                    <p v-if="selectedLaboratoryResultHistory.reason">Lý do: {{ selectedLaboratoryResultHistory.reason
                      }}</p>
                    <article v-for="item in selectedLaboratoryResultHistory.items || []" :key="item.audit_id">
                      <strong>{{ item.test_name }}</strong><small>{{ item.unit || "—" }}</small>
                      <div v-if="item.changed_fields && item.changed_fields.indexOf('result') !== -1">
                        <span>Kết quả</span>
                        <del>{{ historyResultValue(item.before) || "—" }}</del>
                        <b>{{ historyResultValue(item.after) || "—" }}</b></div>
                      <div
                        v-if="item.changed_fields && (item.changed_fields.indexOf('normal_rate') !== -1 || item.changed_fields.indexOf('lower') !== -1 || item.changed_fields.indexOf('upper') !== -1)">
                        <span>Khoảng tham chiếu</span>
                        <del>{{ historyNormalRate(item.before) || "—" }}</del>
                        <b>{{ historyNormalRate(item.after) || "—" }}</b></div>
                    </article>
                  </div>
                </aside>
              </transition>
            </div>
          </section>
        </div>
      </div>
    </el-dialog>

    <el-dialog fullscreen :visible.sync="pdfPreviewVisible" :before-close="closePdfPreview">
      <div class="pdf-preview">
        <iframe v-if="previewUrl" ref="previewFrame" class="pdf-frame" :src="previewUrl" title="Xem trước PDF"
                @load="onPreviewLoaded" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import { TEMPLATE_FORMS } from '@/constants/templateConstant'
import appUtils from '@/utils/appUtils'
import {
  getCachedIndicationResult,
  getIndicationDetailCacheKey,
  getCachedLisResultData,
  updateCachedLisResultData
} from '@/utils/indicationResultsCache'

const categories = [
  { id: 0, name: 'Tất cả', icon: 'el-icon-files' },
  { id: 1, name: 'Xét nghiệm', icon: 'el-icon-data-analysis' },
  { id: 2, name: 'CĐHA', icon: 'el-icon-picture-outline' },
  { id: 4, name: 'Sinh học phân tử', icon: 'el-icon-magic-stick' },
  { id: 3, name: 'Chỉ định khác', icon: 'el-icon-document' }
]

const mergedCategoryOrder = [1, 2, 4, 3]

export default {
  name: 'IndicationOverviewModal',
  props: {
    visible: { type: Boolean, default: false },
    consultation: { type: Object, default: () => ({}) },
    patientVisit: { type: Object, default: () => ({}) },
    initialCategory: { type: Number, default: 0 },
    initialOrderId: { type: [Number, String], default: null },
    initialIndicationId: { type: [Number, String], default: null },
    initialView: { type: String, default: 'order' },
    initialResultScope: { type: String, default: 'single' }
  },
  data () {
    return {
      appUtils,
      isVisible: this.visible,
      selectedCategory: 0,
      selectedOrderId: null,
      selectedIndication: null,
      activeView: 'order',
      orderScope: 'single',
      resultScope: 'single',
      laboratoryResults: [],
      laboratoryMeta: {},
      laboratoryResultsByIndication: {},
      lisResultsByIndication: {},
      localLaboratoryResultOverrides: {},
      localLaboratoryResultReasons: {},
      laboratoryResultChangeSummaries: {},
      historyDrawerVisible: false,
      historyIndicationOrderId: null,
      laboratoryResultHistory: [],
      selectedHistoryChangeSetId: null,
      selectedLaboratoryResultHistory: null,
      loadingResultHistory: false,
      loadingResultHistoryDetail: false,
      isEditingLaboratoryResults: false,
      editResultDrafts: {},
      editLaboratoryResultReason: '',
      focusedResultDraft: { key: '', field: '' },
      savingLaboratoryResults: false,
      indicationDetails: {},
      loadingResults: false,
      loadingIndicationDetail: false,
      rendering: false,
      pdfPreviewVisible: false,
      previewUrl: '',
      pendingPrint: false,
      templates: {},
      templatePromises: {},
      categories
    }
  },
  computed: {
    allOrders () {
      return this.consultation && this.consultation.indication_order ? this.consultation.indication_order : []
    },
    filteredOrders () {
      return this.allOrders.filter(order => order.cate === this.selectedCategory)
    },
    selectedOrder () {
      return this.allOrders.find(order => String(order.id) === String(this.selectedOrderId)) || this.filteredOrders[0] || null
    },
    currentCategory () {
      return this.categories.find(item => item.id === this.selectedCategory) || this.categories[0]
    },
    isLaboratory () {
      return this.selectedCategory === 1
    },
    mergedIndications () {
      const items = this.allOrders.reduce((result, order) => result.concat((order.indication || []).map(item => ({
        ...item,
        cate: order.cate,
        orderId: order.id,
        key: `${order.id}-${item.id}`
      }))), [])
      return items.sort((left, right) => {
        const leftIndex = mergedCategoryOrder.indexOf(Number(left.cate))
        const rightIndex = mergedCategoryOrder.indexOf(Number(right.cate))
        return (leftIndex === -1 ? mergedCategoryOrder.length : leftIndex) - (rightIndex === -1 ? mergedCategoryOrder.length : rightIndex)
      })
    },
    orderDocumentItems () {
      if (this.selectedCategory === 0) return this.mergedIndications
      if (this.orderScope === 'merged') {
        return this.filteredOrders.reduce((items, order) => items.concat((order.indication || []).map(indication => ({
          ...indication,
          orderId: order.id,
          orderName: order.name || `Phiếu chỉ định #${order.id}`,
          key: `${order.id}-${indication.id}`
        }))), [])
      }
      return (this.selectedOrder && this.selectedOrder.indication) || []
    },
    printableItems () {
      return this.activeView === 'order' ? this.orderDocumentItems : this.resultItems
    },
    hasEditableLaboratoryResults () {
      return this.resultItems.some(item => !item.isGroup && item.indicationId && item.storageKey)
    },
    latestManualChange () {
      return this.laboratoryIndicationOrderIds
        .map(id => this.laboratoryResultChangeSummaries[String(id)])
        .filter(Boolean)
        .sort((left, right) => new Date(right.changed_at) - new Date(left.changed_at))[0] || null
    },
    isMergedOrderView () {
      return this.activeView === 'order' && (this.selectedCategory === 0 || this.orderScope === 'merged')
    },
    resultItems () {
      if (this.laboratoryResults.length) return this.laboratoryResults
      return this.resultScope === 'merged' ? this.mergedIndications.filter(item => item.cate === 1) : (this.selectedOrder && this.selectedOrder.indication) || []
    },
    laboratoryIndicationIds () {
      const items = this.resultScope === 'merged' ? this.mergedIndications.filter(item => item.cate === 1) : (this.selectedOrder && this.selectedOrder.indication) || []
      return items.map(item => item.id).filter(Boolean)
    },
    laboratoryIndicationOrderIds () {
      const orders = this.resultScope === 'merged' ? this.allOrders.filter(order => Number(order.cate) === 1) : [this.selectedOrder]
      return orders.map(order => order && order.id).filter(Boolean)
    },
    detailItems () {
      return this.selectedCategory === 0 ? this.mergedIndications : (this.selectedOrder && this.selectedOrder.indication) || []
    },
    printLabel () {
      if (this.isMergedOrderView) return 'In gộp phiếu chỉ định'
      if (this.activeView === 'order') return 'In phiếu chỉ định'
      return this.resultScope === 'merged' ? 'In kết quả gộp' : 'In kết quả'
    },
    isResultDocument () {
      return this.activeView === 'result'
    }
  },
  watch: {
    visible (value) {
      this.isVisible = value
      if (value) this.initialize()
    },
    isVisible (value) {
      if (!value) this.$emit('close')
    },
    initialOrderId () {
      if (this.isVisible) this.initialize()
    },
    activeView () {
      if (this.activeView !== 'result' || !this.isLaboratory) this.cancelEditingLaboratoryResults()
      this.loadLaboratoryResults()
      if (this.activeView === 'result' && this.selectedIndication && !this.isLaboratory) this.loadIndicationDetail(this.selectedIndication.id)
    },
    selectedCategory () {
      this.cancelEditingLaboratoryResults()
      this.loadLaboratoryResults()
    },
    selectedOrderId () {
      this.cancelEditingLaboratoryResults()
      this.loadLaboratoryResults()
    },
    resultScope () {
      this.cancelEditingLaboratoryResults()
      this.loadLaboratoryResults()
    }
  },
  methods: {
    initialize () {
      this.cancelEditingLaboratoryResults()
      this.selectedCategory = this.initialCategory || 0
      this.activeView = this.initialView || 'order'
      this.orderScope = 'single'
      this.resultScope = this.initialResultScope || 'single'
      this.selectedIndication = null
      const order = this.allOrders.find(item => String(item.id) === String(this.initialOrderId)) || this.filteredOrders[0]
      this.selectedOrderId = order && order.id
      if (this.initialIndicationId) this.selectedIndication = this.mergedIndications.find(item => String(item.id) === String(this.initialIndicationId)) || null
      this.$nextTick(() => {
        this.loadLaboratoryResults()
        this.loadResultStatuses()
        if (this.selectedIndication && !this.isLaboratory) this.loadIndicationDetail(this.selectedIndication.id)
        this.scrollToSelectedOrder()
      })
    },
    close () {
      this.cancelEditingLaboratoryResults()
      this.isVisible = false
    },
    selectCategory (category) {
      this.selectedCategory = category
      this.selectedOrderId = this.filteredOrders[0] && this.filteredOrders[0].id
      this.selectedIndication = null
      this.orderScope = 'single'
      if (category !== 1 && this.activeView === 'result') this.activeView = 'order'
      this.$nextTick(this.scrollToSelectedOrder)
    },
    selectOrder (order) {
      this.selectedOrderId = order.id
      this.selectedIndication = null
      this.$nextTick(this.scrollToSelectedOrder)
    },
    selectMergedIndication (item) {
      this.openIndicationResult(item)
    },
    openIndicationResult (indication) {
      const order = indication.orderId
        ? this.allOrders.find(item => String(item.id) === String(indication.orderId))
        : this.allOrders.find(item => (item.indication || []).some(item => String(item.id) === String(indication.id)))

      if (order) {
        this.selectedCategory = Number(order.cate)
        this.selectedOrderId = order.id
      } else if (indication.cate !== undefined) {
        this.selectedCategory = Number(indication.cate)
      }
      this.selectedIndication = indication
      this.resultScope = 'single'
      this.activeView = 'result'
      if (!this.isLaboratory) this.loadIndicationDetail(indication.id)
      this.$nextTick(this.scrollToSelectedOrder)
    },
    categoryCount (category) {
      return category === 0 ? this.allOrders.length : this.allOrders.filter(item => item.cate === category).length
    },
    categoryName (category) {
      const item = this.categories.find(item => item.id === category)
      return item ? item.name : 'Khác'
    },
    formatDate (date) {
      return date ? moment(date).format('DD/MM/YYYY HH:mm') : 'Chưa cập nhật ngày'
    },
    getEditableText (value) {
      return value === null || value === undefined ? '' : String(value)
    },
    resultValue (item) {
      if (Object.prototype.hasOwnProperty.call(item, 'displayResult')) return this.getEditableText(item.displayResult) || 'Chưa có kết quả'
      const result = item.examination_result || {}
      return result.result || result.value || item.value || item.result || 'Chưa có kết quả'
    },
    resultUnit (item) {
      const result = item.examination_result || {}
      return item.unit || result.unit || '—'
    },
    resultNormalRate (item) {
      if (Object.prototype.hasOwnProperty.call(item, 'displayNormalRate')) return item.displayNormalRate || '—'
      const result = item.examination_result || {}
      if (item.normal_rate || result.normal_rate) return item.normal_rate || result.normal_rate
      const lower = item.lower || result.lower
      const upper = item.upper || result.upper
      return lower || upper ? `${lower || '—'} - ${upper || '—'}` : '—'
    },
    resultDevice (item) {
      const result = item.examination_result || {}
      return item.device || result.device || '—'
    },
    indicationStatus (item) {
      if (!item) return { text: 'Chưa có KQ', class: 'status-pending' }
      if (item.status === 3) return { text: 'Đã hủy', class: 'status-cancelled' }

      const laboratoryResult = this.laboratoryResultsByIndication[item.id]
      if (laboratoryResult && laboratoryResult.hasResult) {
        return laboratoryResult.hasAbnormal ? {
          text: 'Có dấu hiệu bất thường',
          class: 'status-abnormal'
        } : { text: 'Đã có KQ', class: 'status-completed' }
      }

      const detail = this.indicationDetails[item.id]
      const result = detail && detail.examination_results
      const lisResult = this.lisResultsByIndication[item.id]
      if (lisResult && (lisResult.result_at || (lisResult.examination_result_values || []).length)) {
        const hasAbnormal = (lisResult.examination_result_values || []).some(value => this.isAbnormalLaboratoryValue(value)?.isAlert)
        return hasAbnormal ? { text: 'Có dấu hiệu bất thường', class: 'status-abnormal' } : {
          text: 'Đã có KQ',
          class: 'status-completed'
        }
      }
      if (result) {
        const conclusion = result.conclusion || {}
        if (Number(conclusion.indentify) === 2) return { text: 'Có dấu hiệu bất thường', class: 'status-abnormal' }
        if (Number(conclusion.indentify) === 3) return { text: 'Không có dấu hiệu bất thường', class: 'status-normal' }
        return { text: 'Đã có KQ', class: 'status-completed' }
      }

      if (item.examination_result && item.examination_result.id) return { text: 'Đã có KQ', class: 'status-completed' }
      return { text: 'Chưa có KQ', class: 'status-pending' }
    },
    orderStatus (order) {
      const indications = order && order.indication ? order.indication : []
      if (!indications.length) return { text: 'Chưa có KQ', class: 'status-pending' }
      const statuses = indications.map(this.indicationStatus)
      if (statuses.every(status => status.class === 'status-cancelled')) {
        return {
          text: 'Đã hủy',
          class: 'status-cancelled'
        }
      }
      if (statuses.some(status => status.class === 'status-abnormal')) {
        return {
          text: 'Có dấu hiệu bất thường',
          class: 'status-abnormal'
        }
      }
      if (statuses.every(status => status.class === 'status-completed' || status.class === 'status-normal' || status.class === 'status-cancelled')) {
        return {
          text: 'Đã có KQ',
          class: 'status-completed'
        }
      }
      if (statuses.some(status => status.class === 'status-completed' || status.class === 'status-normal')) {
        return {
          text: 'Đang có kết quả',
          class: 'status-progress'
        }
      }
      return { text: 'Chưa có KQ', class: 'status-pending' }
    },
    indicationDetail (item) {
      return item ? this.indicationDetails[item.id] || {} : {}
    },
    indicationResultDescription (item) {
      const result = this.indicationDetail(item).examination_results || {}
      return result.conclusion && appUtils.cleanHtml(result.conclusion.description)
    },
    indicationResultConclusion (item) {
      const result = this.indicationDetail(item).examination_results || {}
      return result.conclusion && appUtils.cleanHtml(result.conclusion.conclusion)
    },
    indicationResultIdentify (item) {
      const result = this.indicationDetail(item).examination_results || {}
      return result.conclusion && result.conclusion.indentify
    },
    indicationResultValue (item) {
      const result = this.indicationDetail(item).examination_results || {}
      const lisResult = this.lisResultsByIndication[item.id] || {}
      const lisValues = lisResult.examination_result_values || []
      const values = result.examination_result_values || []
      return lisResult.description || lisResult.result || lisResult.value || (lisValues[0] && lisValues[0].value) || result.result || result.value || (values[0] && values[0].value) || ''
    },
    indicationFiles (item) {
      return this.indicationDetail(item).files || []
    },
    hasResultContent (item) {
      const detail = this.indicationDetail(item)
      const lisResult = this.lisResultsByIndication[item.id]
      const hasLisResult = lisResult && (lisResult.result_at || (lisResult.examination_result_values || []).length)
      return Boolean(detail.examination_results || (detail.files && detail.files.length) || hasLisResult)
    },
    scrollToSelectedOrder () {
      if (!this.selectedOrderId) return
      const ref = this.$refs[`order-card-${this.selectedOrderId}`]
      const card = Array.isArray(ref) ? ref[0] : ref
      if (card && card.scrollIntoView) card.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
    addIndication () {
      this.$emit('add-indication', {
        category: this.selectedCategory || null,
        orderId: this.selectedOrderId,
        view: this.activeView,
        resultScope: this.resultScope
      })
    },
    getApiResponseData (response) {
      return response?.data?.data || response?.data || {}
    },
    async loadLaboratoryResultChangeSummaries () {
      if (!this.$rf || !this.isLaboratory || this.activeView !== 'result') return
      const indicationOrderIds = this.laboratoryIndicationOrderIds
      if (!indicationOrderIds.length) return
      try {
        const response = await this.$rf.getRequest('DoctorRequest').getLisIndicationOrderResultChangeSummaries({ indication_order_ids: indicationOrderIds.join(',') })
        const summaries = this.getApiResponseData(response).items || []
        summaries.forEach(summary => this.$set(this.laboratoryResultChangeSummaries, String(summary.indication_order_id), summary.last_manual_change || null))
      } catch (error) {
        indicationOrderIds.forEach(id => this.$set(this.laboratoryResultChangeSummaries, String(id), null))
      }
    },
    async openLaboratoryResultHistory () {
      this.historyDrawerVisible = true
      this.historyIndicationOrderId = this.historyIndicationOrderId && this.laboratoryIndicationOrderIds.map(String).indexOf(String(this.historyIndicationOrderId)) !== -1
        ? this.historyIndicationOrderId
        : this.laboratoryIndicationOrderIds[0] || null
      await this.loadLaboratoryResultHistory()
    },
    closeLaboratoryResultHistory () {
      this.historyDrawerVisible = false
      this.laboratoryResultHistory = []
      this.selectedHistoryChangeSetId = null
      this.selectedLaboratoryResultHistory = null
    },
    async loadLaboratoryResultHistory () {
      if (!this.historyIndicationOrderId || !this.$rf) return
      try {
        this.loadingResultHistory = true
        this.selectedHistoryChangeSetId = null
        this.selectedLaboratoryResultHistory = null
        const response = await this.$rf.getRequest('DoctorRequest').getLisIndicationOrderResultChangeHistory(this.historyIndicationOrderId, {
          page: 1,
          per_page: 20
        })
        this.laboratoryResultHistory = this.getApiResponseData(response).items || []
      } catch (error) {
        this.laboratoryResultHistory = []
        this.$message({ message: error?.response?.data?.message || 'Không thể tải lịch sử thay đổi.', type: 'error' })
      } finally {
        this.loadingResultHistory = false
      }
    },
    async selectLaboratoryResultHistory (entry) {
      if (!entry || !this.historyIndicationOrderId || !this.$rf) return
      try {
        this.selectedHistoryChangeSetId = entry.change_set_id
        this.loadingResultHistoryDetail = true
        const response = await this.$rf.getRequest('DoctorRequest').getLisIndicationOrderResultChangeHistoryDetail(this.historyIndicationOrderId, entry.change_set_id)
        this.selectedLaboratoryResultHistory = this.getApiResponseData(response)
      } catch (error) {
        this.selectedLaboratoryResultHistory = null
        this.$message({ message: error?.response?.data?.message || 'Không thể tải chi tiết thay đổi.', type: 'error' })
      } finally {
        this.loadingResultHistoryDetail = false
      }
    },
    getLaboratoryResultStorageKey (indicationOrderId) {
      const visitId = this.patientVisit?.id || this.consultation?.patient_visit_id || 'unknown-visit'
      return `laboratory-result-drafts:${visitId}:${indicationOrderId}`
    },
    getLaboratoryValueStorageKey (group, value, index) {
      return String(value.id || value.code || value.service_id || value.examination_id || `${group.id || group.indication_id || 'group'}:${value.name || 'value'}:${index}`)
    },
    getIndicationOrderId (indicationId) {
      const order = this.allOrders.find(item => (item.indication || []).some(indication => String(indication.id) === String(indicationId)))
      return order && order.id
    },
    loadLocalLaboratoryResultOverrides (indicationIds) {
      indicationIds.forEach(indicationId => {
        const key = String(indicationId)
        if (Object.prototype.hasOwnProperty.call(this.localLaboratoryResultOverrides, key) && Object.prototype.hasOwnProperty.call(this.localLaboratoryResultReasons, key)) return
        let values = {}
        let reason = ''
        try {
          const saved = JSON.parse(localStorage.getItem(this.getLaboratoryResultStorageKey(indicationId)) || '{}')
          values = saved && typeof saved.values === 'object' && saved.values ? saved.values : {}
          reason = this.getEditableText(saved && saved.reason)
        } catch (error) {
          // Ignore malformed browser storage and continue with the LIS response.
        }
        this.$set(this.localLaboratoryResultOverrides, key, values)
        this.$set(this.localLaboratoryResultReasons, key, reason)
      })
    },
    getLaboratoryResultOverride (indicationId, storageKey) {
      return (this.localLaboratoryResultOverrides[String(indicationId)] || {})[storageKey] || null
    },
    getReferenceBounds (referenceRange, fallbackLower, fallbackUpper, useOnlyReferenceRange = false) {
      const normalizedRange = this.getEditableText(referenceRange)
        .replace(/(\d)\s*[-–—]\s*(?=\d)/g, '$1|')
        .replace(/,/g, '.')
      const values = normalizedRange.match(/-?\d+(?:\.\d+)?/g) || []
      if (values.length >= 2) return { lower: Number(values[0]), upper: Number(values[1]) }
      return useOnlyReferenceRange ? { lower: undefined, upper: undefined } : {
        lower: fallbackLower,
        upper: fallbackUpper
      }
    },
    getLaboratoryResultValue (value) {
      const candidates = [value.result, value.result_value, value.value]
      const populatedValue = candidates.find(candidate => candidate !== null && candidate !== undefined && this.getEditableText(candidate).trim() !== '')
      return populatedValue === undefined ? '' : populatedValue
    },
    historyResultValue (snapshot) {
      return snapshot ? this.getLaboratoryResultValue(snapshot) : ''
    },
    historyNormalRate (snapshot) {
      if (!snapshot) return ''
      if (this.getEditableText(snapshot.normal_rate).trim()) return snapshot.normal_rate
      const hasLower = snapshot.lower !== null && snapshot.lower !== undefined
      const hasUpper = snapshot.upper !== null && snapshot.upper !== undefined
      return hasLower && hasUpper ? `${snapshot.lower} - ${snapshot.upper}` : ''
    },
    createLaboratoryResultItem (group, value, index) {
      const indicationId = group.indication_id
      const indicationOrderId = this.getIndicationOrderId(indicationId)
      const storageKey = this.getLaboratoryValueStorageKey(group, value, index)
      const rawNormalRate = value.normal_rate || (value.lower || value.upper ? `${value.lower || '—'} - ${value.upper || '—'}` : '')
      const displayResult = this.getLaboratoryResultValue(value)
      const displayNormalRate = rawNormalRate
      const bounds = this.getReferenceBounds(displayNormalRate, value.lower, value.upper)
      const effectiveValue = {
        ...value,
        value: displayResult,
        normal_rate: displayNormalRate,
        lower: bounds.lower,
        upper: bounds.upper
      }
      return {
        ...effectiveValue,
        id: value.id || `${group.id || indicationId}-${storageKey}`,
        name: value.name || group.name || group.description,
        note: value.note || group.note || '',
        device: group.device || value.device || '',
        indicationId,
        indicationOrderId,
        storageKey,
        displayResult,
        displayNormalRate,
        isAlert: this.isAbnormalLaboratoryValue(effectiveValue)?.isAlert,
        typeAlert: value.alert_type || this.isAbnormalLaboratoryValue(effectiveValue)?.typeAlert
      }
    },
    startEditingLaboratoryResults () {
      if (!this.hasEditableLaboratoryResults) return
      const drafts = {}
      this.resultItems.filter(item => !item.isGroup && item.indicationId && item.storageKey).forEach(item => {
        const savedDraft = this.getLaboratoryResultOverride(item.indicationOrderId, item.storageKey) || {}
        drafts[item.key || item.id] = {
          result: Object.prototype.hasOwnProperty.call(savedDraft, 'result') ? savedDraft.result : this.getEditableText(item.displayResult),
          normalRate: Object.prototype.hasOwnProperty.call(savedDraft, 'normalRate') ? savedDraft.normalRate : this.getEditableText(item.displayNormalRate),
          version: savedDraft.version || item.version,
          editedFields: savedDraft.editedFields || {}
        }
      })
      this.editResultDrafts = drafts
      const indicationOrderIds = [...new Set(this.resultItems.filter(item => !item.isGroup && item.indicationOrderId).map(item => String(item.indicationOrderId)))]
      const savedReasons = [...new Set(indicationOrderIds.map(id => this.localLaboratoryResultReasons[id] || '').filter(Boolean))]
      this.editLaboratoryResultReason = savedReasons.length === 1 ? savedReasons[0] : ''
      this.isEditingLaboratoryResults = true
    },
    editResultValue (item) {
      return this.getEditableText((this.editResultDrafts[item.key || item.id] || {}).result)
    },
    editResultNormalRate (item) {
      return this.getEditableText((this.editResultDrafts[item.key || item.id] || {}).normalRate)
    },
    resultInputClass (item, field) {
      const key = item.key || item.id
      const draft = this.editResultDrafts[key] || {}
      const initialValue = field === 'result' ? item.displayResult : item.displayNormalRate
      const savedOverride = this.getLaboratoryResultOverride(item.indicationOrderId, item.storageKey)
      return {
        'result-input--focused': this.focusedResultDraft.key === key && this.focusedResultDraft.field === field,
        // displayResult/displayNormalRate already include the saved override,
        // therefore use its persisted presence to retain the visual marker
        // when the user reopens edit mode.
        'result-input--changed': Boolean(savedOverride && (savedOverride.editedFields ? savedOverride.editedFields[field] : true)) || this.getEditableText(draft[field]) !== this.getEditableText(initialValue)
      }
    },
    setFocusedResultDraft (item, field) {
      this.focusedResultDraft = { key: item.key || item.id, field }
    },
    clearFocusedResultDraft () {
      this.focusedResultDraft = { key: '', field: '' }
    },
    updateResultDraft (item, field, value) {
      const key = item.key || item.id
      const draft = this.editResultDrafts[key] || {
        result: this.getEditableText(item.displayResult),
        normalRate: this.getEditableText(item.displayNormalRate),
        version: item.version,
        editedFields: {}
      }
      this.$set(draft, field, value)
      this.$set(draft.editedFields, field, this.getEditableText(value) !== this.getEditableText(field === 'result' ? item.displayResult : item.displayNormalRate))
      this.$set(this.editResultDrafts, key, draft)
      this.persistLaboratoryResultDrafts()
    },
    cancelEditingLaboratoryResults () {
      this.isEditingLaboratoryResults = false
      this.editResultDrafts = {}
      this.editLaboratoryResultReason = ''
      this.clearFocusedResultDraft()
    },
    async confirmSaveLaboratoryResults () {
      try {
        await this.$confirm('Bạn có chắc chắn muốn cập nhật kết quả xét nghiệm?', 'Xác nhận cập nhật', {
          confirmButtonText: 'Cập nhật',
          cancelButtonText: 'Hủy',
          type: 'warning'
        })
        await this.saveLaboratoryResults()
      } catch (error) {
        // User cancelled the confirmation; retain the edit form and its draft.
      }
    },
    async confirmCancelEditingLaboratoryResults () {
      try {
        await this.$confirm('Bạn có chắc chắn muốn hủy chỉnh sửa? Các thay đổi chưa cập nhật sẽ bị bỏ qua.', 'Xác nhận hủy chỉnh sửa', {
          confirmButtonText: 'Đồng ý hủy',
          cancelButtonText: 'Tiếp tục chỉnh sửa',
          type: 'warning'
        })
        this.discardLaboratoryResultDrafts()
        this.cancelEditingLaboratoryResults()
      } catch (error) {
        // User keeps editing; do not mutate the draft.
      }
    },
    getEditingLaboratoryItems () {
      return this.resultItems.filter(item => !item.isGroup && item.indicationId && item.indicationOrderId && item.storageKey)
    },
    persistLaboratoryResultDrafts () {
      if (!this.isEditingLaboratoryResults) return
      const itemsByIndication = this.getEditingLaboratoryItems().reduce((result, item) => {
        const indicationOrderId = String(item.indicationOrderId)
        if (!result[indicationOrderId]) result[indicationOrderId] = []
        result[indicationOrderId].push(item)
        return result
      }, {})
      Object.keys(itemsByIndication).forEach(indicationOrderId => {
        const values = {}
        itemsByIndication[indicationOrderId].forEach(item => {
          const draft = this.editResultDrafts[item.key || item.id] || {}
          const editedFields = draft.editedFields || {}
          if (!editedFields.result && !editedFields.normalRate) return
          values[item.storageKey] = {
            result: this.getEditableText(draft.result),
            normalRate: this.getEditableText(draft.normalRate),
            version: draft.version || item.version,
            editedFields,
            updatedAt: new Date().toISOString()
          }
        })
        const reason = this.editLaboratoryResultReason.trim()
        if (!Object.keys(values).length && !reason) {
          localStorage.removeItem(this.getLaboratoryResultStorageKey(indicationOrderId))
          this.$delete(this.localLaboratoryResultOverrides, indicationOrderId)
          this.$delete(this.localLaboratoryResultReasons, indicationOrderId)
          return
        }
        localStorage.setItem(this.getLaboratoryResultStorageKey(indicationOrderId), JSON.stringify({
          version: 2,
          indicationOrderId,
          values,
          reason,
          updatedAt: new Date().toISOString()
        }))
        this.$set(this.localLaboratoryResultOverrides, indicationOrderId, values)
        this.$set(this.localLaboratoryResultReasons, indicationOrderId, reason)
      })
    },
    updateLaboratoryResultReason (value) {
      this.editLaboratoryResultReason = value
      this.persistLaboratoryResultDrafts()
    },
    discardLaboratoryResultDrafts () {
      const indicationOrderIds = [...new Set(this.getEditingLaboratoryItems().map(item => String(item.indicationOrderId)))]
      indicationOrderIds.forEach(indicationOrderId => {
        localStorage.removeItem(this.getLaboratoryResultStorageKey(indicationOrderId))
        this.$delete(this.localLaboratoryResultOverrides, indicationOrderId)
        this.$delete(this.localLaboratoryResultReasons, indicationOrderId)
      })
    },
    clearLaboratoryResultDraft (indicationOrderId) {
      localStorage.removeItem(this.getLaboratoryResultStorageKey(indicationOrderId))
      this.$delete(this.localLaboratoryResultOverrides, String(indicationOrderId))
      this.$delete(this.localLaboratoryResultReasons, String(indicationOrderId))
    },
    rebaseLaboratoryResultDraftVersions () {
      this.getEditingLaboratoryItems().forEach(item => {
        const draft = this.editResultDrafts[item.key || item.id]
        if (draft && (draft.editedFields?.result || draft.editedFields?.normalRate)) this.$set(draft, 'version', item.version)
      })
      this.persistLaboratoryResultDrafts()
    },
    getUpdatedLaboratoryGroups (response) {
      return response?.data?.data?.Data || response?.data?.Data || []
    },
    applyUpdatedLaboratoryGroups (groups) {
      groups.forEach(group => {
        if (!group.indication_id) return
        updateCachedLisResultData(group.indication_id, () => group)
        this.$set(this.lisResultsByIndication, group.indication_id, group)
      })
      this.updateLaboratoryResultStatus(groups)
    },
    async saveLaboratoryResults () {
      if (!this.isEditingLaboratoryResults || this.savingLaboratoryResults) return
      try {
        this.savingLaboratoryResults = true
        const updatesByIndicationOrder = {}
        this.getEditingLaboratoryItems().forEach(item => {
          const indicationOrderId = String(item.indicationOrderId)
          const draft = this.editResultDrafts[item.key || item.id] || {}
          if (!draft.editedFields?.result && !draft.editedFields?.normalRate) return
          if (!updatesByIndicationOrder[indicationOrderId]) updatesByIndicationOrder[indicationOrderId] = []
          const bounds = this.getReferenceBounds(draft.normalRate, null, null, true)
          updatesByIndicationOrder[indicationOrderId].push({
            examination_result_value_id: item.id,
            result: this.getEditableText(draft.result),
            normal_rate: this.getEditableText(draft.normalRate),
            lower: Number.isFinite(bounds.lower) ? bounds.lower : null,
            upper: Number.isFinite(bounds.upper) ? bounds.upper : null,
            version: draft.version || item.version
          })
        })
        const indicationOrderIds = Object.keys(updatesByIndicationOrder)
        if (!indicationOrderIds.length) {
          this.$message({ message: 'Chưa có thay đổi kết quả để cập nhật.', type: 'warning' })
        } else {
          const saveNextIndication = index => {
            if (index >= indicationOrderIds.length) return Promise.resolve()
            const indicationOrderId = indicationOrderIds[index]
            return this.$rf.getRequest('DoctorRequest').updateLisIndicationOrderResults(indicationOrderId, {
              items: updatesByIndicationOrder[indicationOrderId],
              reason: this.editLaboratoryResultReason.trim(),
              client_updated_at: new Date().toISOString()
            }).then(response => {
              const groups = this.getUpdatedLaboratoryGroups(response)
              if (!groups.length) return Promise.reject(new Error('Máy chủ không trả dữ liệu kết quả sau khi cập nhật.'))
              this.applyUpdatedLaboratoryGroups(groups)
              this.clearLaboratoryResultDraft(indicationOrderId)
              return saveNextIndication(index + 1)
            })
          }
          await saveNextIndication(0)
          this.cancelEditingLaboratoryResults()
          await this.loadLaboratoryResults()
          if (this.historyDrawerVisible) {
            const changeSetId = this.selectedHistoryChangeSetId
            await this.loadLaboratoryResultHistory()
            const entry = this.laboratoryResultHistory.find(item => item.change_set_id === changeSetId)
            if (entry) await this.selectLaboratoryResultHistory(entry)
          }
          this.$message({ message: 'Đã cập nhật kết quả xét nghiệm.', type: 'success' })
        }
      } catch (error) {
        const status = error?.response?.status
        if (status === 409) {
          try {
            await this.$confirm('Kết quả đã được cập nhật bởi người khác hoặc phiếu không còn cho phép sửa. Bạn có muốn tải dữ liệu mới? Bản nháp sẽ được giữ lại để bạn đối chiếu.', 'Xung đột dữ liệu', {
              confirmButtonText: 'Tải lại dữ liệu',
              cancelButtonText: 'Tiếp tục chỉnh sửa',
              type: 'warning'
            })
            await this.loadLaboratoryResults()
            this.rebaseLaboratoryResultDraftVersions()
          } catch (confirmError) {
            // Keep the form and the persisted draft untouched.
          }
        }
        const message = error?.response?.data?.message || error?.message || 'Không thể cập nhật kết quả xét nghiệm. Bản nháp đã được giữ lại.'
        this.$message({ message, type: 'error' })
      } finally {
        this.savingLaboratoryResults = false
      }
    },
    async loadLaboratoryResults () {
      this.laboratoryResults = []
      if (!this.isVisible || this.activeView !== 'result' || !this.isLaboratory || !this.laboratoryIndicationIds.length || !this.$rf) return

      try {
        this.loadingResults = true
        const indicationIds = this.laboratoryIndicationIds
        this.loadLocalLaboratoryResultOverrides(this.laboratoryIndicationOrderIds)
        const res = await getCachedLisResultData(indicationIds, () => this.$rf.getRequest('DoctorRequest').getLisIndicationsResultData({ indication_ids: indicationIds.join(',') }))
        const groups = (res && res.data && res.data.Data) ? res.data.Data : []
        this.laboratoryMeta = (res && res.data && res.data.meta) || {}
        this.updateLaboratoryResultStatus(groups)
        this.updateLisResultData(groups)
        this.laboratoryResults = groups.reduce((items, group, groupIndex) => {
          const values = group.examination_result_values || []
          const groupName = group.name || group.description
          if (!values.length) {
            return items.concat({
              id: group.indication_id || group.id,
              name: groupName,
              result: 'Chưa có kết quả',
              note: group.note || ''
            })
          }
          const showGroup = groups.length === 1 || values.length > 1
          const rows = values.map((value, valueIndex) => this.createLaboratoryResultItem(group, value, valueIndex))
          return showGroup ? items.concat({
            id: `group-${group.id || groupIndex}`,
            name: groupName,
            isGroup: true
          }, rows) : items.concat(rows)
        }, [])
        this.loadLaboratoryResultChangeSummaries()
      } catch (error) {
        this.laboratoryResults = []
      } finally {
        this.loadingResults = false
      }
    },
    async loadResultStatuses () {
      if (!this.isVisible || !this.$rf) return

      const indicationIds = this.mergedIndications
        .filter(indication => Number(indication.cate) === 1)
        .map(indication => indication.id)
        .filter(Boolean)

      if (indicationIds.length) {
        try {
          this.loadLocalLaboratoryResultOverrides(this.laboratoryIndicationOrderIds)
          const res = await getCachedLisResultData(indicationIds, () => this.$rf.getRequest('DoctorRequest').getLisIndicationsResultData({ indication_ids: indicationIds.join(',') }))
          const groups = (res && res.data && res.data.Data) ? res.data.Data : []
          this.laboratoryMeta = (res && res.data && res.data.meta) || {}
          this.updateLaboratoryResultStatus(groups)
          this.updateLisResultData(groups)
        } catch (error) {
          // The indication detail endpoint still provides non-LIS results.
        }
      }

      const detailIndications = this.mergedIndications.filter(indication => indication.cate !== 1 && !this.indicationDetails[indication.id])
      await Promise.all(detailIndications.map(indication => this.loadIndicationDetail(indication.id, false)))
    },
    updateLaboratoryResultStatus (groups) {
      groups.forEach(group => {
        const indicationId = group.indication_id
        if (!indicationId) return
        const values = group.examination_result_values || []
        const hasAbnormal = values.some(value => {
          const referenceRange = value.normal_rate || (value.lower || value.upper ? `${value.lower || '—'} - ${value.upper || '—'}` : '')
          const bounds = this.getReferenceBounds(referenceRange, value.lower, value.upper)
          const resultValue = this.getLaboratoryResultValue(value)
          return this.isAbnormalLaboratoryValue({
            ...value,
            value: resultValue,
            lower: bounds.lower,
            upper: bounds.upper
          })?.isAlert
        })
        this.$set(this.laboratoryResultsByIndication, indicationId, {
          hasResult: Boolean(group.result_at || values.length),
          hasAbnormal
        })
      })
    },
    updateLisResultData (groups) {
      groups.forEach(group => {
        if (group.indication_id) this.$set(this.lisResultsByIndication, group.indication_id, group)
      })
    },
    isAbnormalLaboratoryValue (value, recalculate = false) {
      if (!recalculate && (value.is_ncs === 1 || value.is_alert)) return { isAlert: true, typeAlert: '' }
      const numericValue = Number(value.value)
      const lower = Number(value.lower)
      const upper = Number(value.upper)
      if (Number.isNaN(numericValue) || Number.isNaN(lower) || Number.isNaN(upper) || lower === upper) {
        return {
          isAlert: false,
          typeAlert: ''
        }
      }
      return {
        isAlert: numericValue < lower || numericValue > upper,
        typeAlert: numericValue < lower ? 'L' : numericValue > upper ? 'H' : ''
      }
    },
    async loadIndicationDetail (id, showLoading = true) {
      if (!id || !this.$rf || this.indicationDetails[id]) return this.indicationDetails[id]

      try {
        if (showLoading) this.loadingIndicationDetail = true
        const [detailResponse, lisResponse] = await Promise.all([
          getCachedIndicationResult(getIndicationDetailCacheKey(id), () => this.$rf.getRequest('DoctorRequest').getIndicationById(id).then(response => response?.data)),
          getCachedLisResultData([id], () => this.$rf.getRequest('DoctorRequest').getLisIndicationsResultData({ indication_ids: id }))
        ])
        const lisGroups = (lisResponse && lisResponse.data && lisResponse.data.Data) ? lisResponse.data.Data : []
        this.laboratoryMeta = (lisResponse && lisResponse.data && lisResponse.data.meta) || this.laboratoryMeta
        this.updateLaboratoryResultStatus(lisGroups)
        this.updateLisResultData(lisGroups)
        if (detailResponse) this.$set(this.indicationDetails, id, detailResponse)
        return detailResponse
      } catch (error) {
        return null
      } finally {
        if (showLoading) this.loadingIndicationDetail = false
      }
    },
    getDocumentTemplateId () {
      return this.isResultDocument ? TEMPLATE_FORMS.MAU_PHIEU_KET_QUA_XN : TEMPLATE_FORMS.MAU_PHIEU_CHI_DINH_CLS
    },
    async loadDocumentTemplate () {
      const templateId = this.getDocumentTemplateId()
      if (this.templates[templateId]) return this.templates[templateId]
      if (!this.templatePromises[templateId]) {
        const request = this.$rf.getRequest('CommonRequest')
        this.$set(this.templatePromises, templateId, request.getTemplates(templateId)
          .then(response => {
            const template = response?.data || null
            this.$set(this.templates, templateId, template)
            return template
          })
          .finally(() => {
            this.$delete(this.templatePromises, templateId)
          }))
      }
      return this.templatePromises[templateId]
    },
    getPreviewUrl (previewPath) {
      if (!previewPath) return ''
      if (/^https?:\/\//i.test(previewPath)) return `${previewPath}${previewPath.includes('?') ? '&' : '?'}t=${Date.now()}`
      const endpoint = String(process.env.VUE_APP_PRINT_PDF_TEMPLATE_ENDPOINT || '').replace(/\/$/, '')
      const path = String(previewPath).startsWith('/') ? previewPath : `/${previewPath}`
      return `${endpoint}${path}?t=${Date.now()}`
    },
    getDocumentStorageKey () {
      const visitId = this.patientVisit?.id || this.consultation?.patient_visit_id || 'unknown-visit'
      const documentType = this.isResultDocument ? 'laboratory-result' : 'indication-order'
      const scope = this.isResultDocument ? this.resultScope : (this.isMergedOrderView ? 'merged' : 'single')
      const orderId = scope === 'single' ? this.selectedOrder?.id || 'unknown-order' : 'all-orders'
      return `patient-visit-document:${visitId}:${documentType}:${scope}:${orderId}`
    },
    handleSaveDocumentLink (documentLink) {
      if (!documentLink) throw new Error('Rendered document URL is missing')
      // This is intentionally isolated from rendering so it can be replaced by
      // the corresponding document_link API once the backend endpoint exists.
      localStorage.setItem(this.getDocumentStorageKey(), JSON.stringify({
        url: documentLink,
        savedAt: new Date().toISOString(),
        orderId: this.selectedOrder?.id || null,
        scope: this.isResultDocument ? this.resultScope : (this.isMergedOrderView ? 'merged' : 'single')
      }))
      return documentLink
    },
    getDocumentValues () {
      const person = this.patientVisit?.person || this.consultation?.person || {}
      const documentDate = moment()
      const laboratoryMeta = this.getLaboratoryMeta()
      const diagnosis = this.consultation?.icd_name || this.patientVisit?.icd_name || this.consultation?.diagnose || ''
      return {
        patient_name: person.name || '',
        patient_code: person.pid || person.code || '',
        dob: person.birthday ? moment(person.birthday).format('DD/MM/YYYY') : '',
        gender: person.gender === 1 ? 'Nam' : person.gender === 2 ? 'Nữ' : person.gender === 3 ? 'Khác' : '',
        nationality: person.nationality || '',
        address: person.address || '',
        phone: person.phone || '',
        registration_time: this.patientVisit?.created_at ? moment(this.patientVisit.created_at).format('DD/MM/YYYY HH:mm') : '',
        doctor_name: this.$user?.name || this.selectedOrder?.user_name || '',
        diagnose: diagnosis,
        sampler_name: laboratoryMeta.sample_taker_name || '',
        sample_time: laboratoryMeta.take_sample_at || '',
        receiver_name: laboratoryMeta.sample_receiver_name || '',
        receive_time: laboratoryMeta.received_sample_at || '',
        login_username: '',
        login_password: '',
        print_time: documentDate.format('DD/MM/YYYY HH:mm'),
        order_name: this.selectedOrder?.name || '',
        date: documentDate.format('DD'),
        month: documentDate.format('MM'),
        year: documentDate.format('YYYY'),
        lab_officer_name: ''
      }
    },
    getLaboratoryMeta () {
      return this.laboratoryMeta || {}
    },
    getName (item, keys) {
      if (!item) return ''
      for (const key of keys) {
        const value = key.split('.').reduce((source, part) => source && source[part], item)
        if (typeof value === 'string' && value.trim()) return value.trim()
      }
      return ''
    },
    getIndicationGroupName (indication) {
      return this.getName(indication, [
        'group_name', 'indication_group_name', 'service_group_name',
        'group.name', 'indication_group.name', 'service_group.name',
        'service_catalog.group_name', 'service_catalog.indication_group_name',
        'service_catalog.group.name', 'service_catalog.indication_group.name',
        'service_catalog.service_category.name', 'service_category.name',
        'service_catalog.services_category.name', 'services_category.name'
      ])
    },
    getIndicationSubgroupName (indication) {
      return this.getName(indication, [
        'subgroup_name', 'sub_group_name', 'indication_subgroup_name', 'indication_category_name',
        'subgroup.name', 'sub_group.name', 'indication_subgroup.name', 'indication_category.name',
        'service_catalog.subgroup_name', 'service_catalog.sub_group_name',
        'service_catalog.indication_subgroup_name', 'service_catalog.indication_category_name',
        'service_catalog.subgroup.name', 'service_catalog.sub_group.name',
        'service_catalog.indication_subgroup.name', 'service_catalog.indication_category.name'
      ])
    },
    getResultIndications () {
      return this.resultScope === 'merged'
        ? this.mergedIndications.filter(item => item.cate === 1)
        : (this.selectedOrder?.indication || [])
    },
    appendGroupHeaders (rows, indication, previousGroup, previousSubgroup, fallbackSubgroup = '') {
      const groupName = this.getIndicationGroupName(indication)
      const subgroupName = this.getIndicationSubgroupName(indication) || fallbackSubgroup
      if (groupName && groupName !== previousGroup) rows.push({ group_name: groupName })
      if (subgroupName && (groupName !== previousGroup || subgroupName !== previousSubgroup)) rows.push({ subgroup_name: subgroupName })
      return { groupName: groupName || previousGroup, subgroupName: subgroupName || previousSubgroup }
    },
    buildLaboratoryResultRows () {
      const indications = this.getResultIndications()
      const indicationById = indications.reduce((items, indication) => {
        items[String(indication.id)] = indication
        return items
      }, {})
      const results = indications
        .map(indication => this.lisResultsByIndication[indication.id])
        .filter(Boolean)
      const sourceResults = results.length
        ? results
        : this.resultItems.filter(item => !item.isGroup).map(item => ({
          indication_id: item.indicationId,
          name: item.name,
          device: item.device,
          examination_result_values: [item]
        }))
      const rows = []
      let previousGroup = ''
      let previousSubgroup = ''

      sourceResults.forEach(result => {
        const indication = indicationById[String(result.indication_id)] || result
        const values = result.examination_result_values || []
        const headers = this.appendGroupHeaders(
          rows,
          indication,
          previousGroup,
          previousSubgroup,
          values.length > 1 ? result.name || indication.name || '' : ''
        )
        previousGroup = headers.groupName
        previousSubgroup = headers.subgroupName
        const resultValues = values.length ? values : [result]
        resultValues.forEach((value, valueIndex) => {
          const effectiveValue = this.createLaboratoryResultItem(result, value, valueIndex)
          const normalRate = this.resultNormalRate(effectiveValue)
          const typeAlert = effectiveValue.typeAlert
          rows.push({
            service_name: effectiveValue.name || result.name || indication.name || '',
            result: this.getEditableText(effectiveValue.displayResult),
            // These fields are checkbox values in the document template, so they
            // must be actual booleans rather than the display marker (L/H).
            lower: typeAlert === 'L',
            higher: typeAlert === 'H',
            is_alert: Boolean(effectiveValue.isAlert),
            reference_range: normalRate,
            unit: effectiveValue.unit || result.unit || '',
            machine: result.device || effectiveValue.device || '',
            note: effectiveValue.note || result.note || ''
          })
        })
      })
      return rows
    },
    buildDocumentPayload () {
      const values = this.getDocumentValues()
      if (this.isResultDocument) {
        return { values, repeats: { xn: this.buildLaboratoryResultRows() } }
      }
      let previousGroup = ''
      let previousSubgroup = ''
      let index = 0
      const indications = this.orderDocumentItems.reduce((rows, item) => {
        const headers = this.appendGroupHeaders(rows, item, previousGroup, previousSubgroup)
        previousGroup = headers.groupName
        previousSubgroup = headers.subgroupName
        index += 1
        rows.push({
          stt: String(index),
          service_name: item.name || '',
          qty: String(item.qty || 1),
          location: item.target_name || ''
          // due_date: item.due_date ? moment(item.due_date).format('DD/MM/YYYY') : ''
        })
        return rows
      }, [])
      return { values, repeats: { cls: indications } }
    },
    async renderDocument () {
      const template = await this.loadDocumentTemplate()
      if (!template?.id) throw new Error('Indication document template is missing')
      const response = await this.$rf.getRequest('CommonRequest').renderDocument(template.id, this.buildDocumentPayload())
      const documentLink = this.getPreviewUrl(response?.data?.preview_url)
      if (!documentLink) throw new Error('Rendered document URL is missing')
      this.previewUrl = documentLink
      return documentLink
    },
    async handlePrintDocument () {
      if (!this.printableItems.length || this.rendering) return
      try {
        this.rendering = true
        const documentLink = await this.renderDocument()
        this.handleSaveDocumentLink(documentLink)
        this.openPdfPreview(false)
      } catch (error) {
        console.log(error)
        this.$message({ message: this.$t('Không thể tạo phiếu in'), type: 'error' })
      } finally {
        this.rendering = false
      }
    },
    openPdfPreview (print = false) {
      this.pendingPrint = print
      this.pdfPreviewVisible = Boolean(this.previewUrl)
    },
    onPreviewLoaded () {
      if (!this.pendingPrint || !this.$refs.previewFrame) return
      this.pendingPrint = false
      const previewOrigin = new URL(this.previewUrl, window.location.href).origin
      if (previewOrigin === window.location.origin) {
        this.$refs.previewFrame.contentWindow.focus()
        this.$refs.previewFrame.contentWindow.print()
        return
      }
      const printWindow = window.open(this.previewUrl, '_blank', 'noopener,noreferrer')
      if (!printWindow) {
        this.$message({
          message: this.$t('Trình duyệt đã chặn cửa sổ in. Hãy cho phép popup để in phiếu.'),
          type: 'warning'
        })
      }
    },
    closePdfPreview () {
      this.pdfPreviewVisible = false
      this.previewUrl = ''
    }
  }
}
</script>

<style lang="scss">
.indication-overview-dialog {
  margin-bottom: 24px;
  border-radius: 16px;
  overflow: hidden;

  .el-dialog__header {
    padding: 0;
  }

  .el-dialog__headerbtn {
    z-index: 2;
    top: 4px;
    right: 10px;
    font-size: 24px;
  }

  .el-dialog__body {
    padding: 0;
  }
}

.indication-overview {
  color: #172b4d;
  background: #f7f9fc;
  min-height: calc(100vh - 48px);
}

.pdf-preview {
  height: calc(100vh - 80px);
  min-height: 720px;
  background: #d9dde3;
  overflow: hidden;
}

.pdf-frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}

.eyebrow {
  margin: 0 0 4px;
  color: #7d8ba1;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
}

.panel-toolbar h3, .result-detail h2 {
  margin: 0;
  color: #1b3b70;
  font-size: 24px;
  font-weight: 700;
}

.overview-body {
  height: calc(100vh - 48px);
  display: grid;
  grid-template-columns: 238px minmax(300px, .85fr) minmax(440px, 1.45fr);
  overflow: hidden;
}

.category-menu {
  background: #fff;
  padding: 20px 12px;
  border-right: 1px solid #e8edf5;
  overflow-y: auto;
}

.category-item {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 4px;
  border-radius: 8px;
  color: #53637d;
  cursor: pointer;
  font-size: 14px;
}

.category-item i {
  font-size: 17px;
}

.category-item b {
  min-width: 22px;
  text-align: center;
  padding: 2px 6px;
  color: #7d8ba1;
  background: #f0f3f8;
  border-radius: 10px;
  font-size: 11px;
}

.category-item.active {
  color: #2864d7;
  background: #edf4ff;
  font-weight: 700;
}

.category-item.active b {
  background: #d6e7ff;
  color: #2864d7;
}

.order-panel, .preview-panel {
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.order-panel {
  border-right: 1px solid #e3e9f2;
  background: #f7f9fc;
}

.panel-toolbar, .preview-toolbar {
  flex: 0 0 auto;
  min-height: 106px;
  padding: 24px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: #fff;
  border-bottom: 1px solid #e3e9f2;
}

.preview-toolbar__left, .preview-toolbar__right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.preview-toolbar__right {
  justify-content: flex-end;
}

.view-switch, .result-scope {
  display: flex;
  background: #eef2f7;
  padding: 3px;
  border-radius: 7px;
}

.view-switch button, .result-scope button {
  border: 0;
  background: transparent;
  padding: 6px 9px;
  border-radius: 5px;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
}

.view-switch .active, .result-scope .active {
  color: #255fc9;
  background: #fff;
  box-shadow: 0 1px 3px rgba(20, 46, 89, .12);
  font-weight: 700;
}

.order-content, .preview-content {
  min-height: 0;
  flex: 1;
  overflow: auto;
}

.order-content {
  padding: 22px 20px;
}

.preview-content {
  padding: 22px 20px;
  background: #fff;
}

.order-list {
  margin-top: 0px;
}

.order-card {
  padding: 16px;
  margin-bottom: 12px;
  background: #fff;
  border: 1px solid #e4eaf3;
  border-radius: 10px;
  cursor: pointer;
  transition: .18s ease;
}

.order-card:hover, .order-card.selected {
  border-color: #5b91ed;
  box-shadow: 0 5px 14px rgba(42, 103, 210, .12);
}

.order-card.selected {
  background: #f8fbff;
}

.order-card__top {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.order-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: #2c69db;
  background: #e8f1ff;
  border-radius: 8px;
}

.order-card h4 {
  margin: 0;
  color: #2b4163;
  line-height: 1.35;
  font-size: 14px;
}

.order-card p {
  margin: 4px 0 0;
  color: #8b98aa;
  font-size: 11px;
}

.status-badge {
  margin-left: auto;
  padding: 4px 7px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.status-completed {
  color: #247650;
  background: #e9f7f0;
}

.status-normal {
  color: #2864d7;
  background: #edf4ff;
}

.status-abnormal {
  color: #bd3c3c;
  background: #fff0f0;
}

.status-cancelled {
  color: #8a5a5a;
  background: #f5ecec;
}

.status-pending {
  color: #9b7619;
  background: #fff8e5;
}

.status-progress {
  color: #7b5bb2;
  background: #f4efff;
}

.order-card__footer {
  border-top: 1px solid #edf0f5;
  margin-top: 13px;
  padding-top: 10px;
  display: flex;
  justify-content: space-between;
  gap: 6px;
  color: #8290a4;
  font-size: 11px;
}

.merged-orders {
  margin-top: 0;
}

.merged-order-item {
  width: 100%;
  padding: 13px 10px;
  display: grid;
  grid-template-columns: 10px 1fr auto;
  align-items: center;
  gap: 9px;
  border: 0;
  border-bottom: 1px solid #e8edf5;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: #2d425f;
  font-size: 14px;
}

.merged-order-item:hover {
  background: #edf4ff;
}

.merged-order-item small {
  color: #8b98aa;
  font-size: 11px;
}

.order-dot {
  width: 6px;
  height: 6px;
  background: #4f87e4;
  border-radius: 50%;
}

.indication-content-list, .result-content, .result-detail {
  max-width: 960px;
  margin: 0 auto;
}

.indication-content-list {
  border: 1px solid #dfe5ee;
  border-radius: 8px;
  overflow: hidden;
}

.indication-content-item {
  min-height: 56px;
  display: grid;
  cursor: pointer;
  grid-template-columns: 34px 1fr;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  border-bottom: 1px solid #e8edf5;
  font-size: 14px;
}

.indication-content-item:last-child {
  border-bottom: 0;
}

.indication-content-item > span {
  color: #6285bf;
  font-weight: 700;
}

.indication-content-item strong, .indication-content-item small {
  display: block;
}

.indication-content-item small {
  margin-top: 3px;
  color: #7f8da3;
}

.result-table {
  min-width: 800px;
  border: 1px solid #cbd5e1;
}

.result-content {
  overflow-x: auto;
}

.result-row {
  min-height: 46px;
  display: grid;
  grid-template-columns: 2fr 1fr .7fr 1.15fr 1fr 1.2fr;
  border-bottom: 1px solid #dce4ef;
  align-items: center;
}

.result-group-row {
  display: block;
  min-height: 36px;
  background: #f7f9fc;
  color: #364d70;
}

.result-group-row strong {
  display: block;
  padding: 10px;
  font-size: 13px;
}

.result-row:last-child {
  border-bottom: 0;
}

.result-row > * {
  min-width: 0;
  padding: 10px;
  border-right: 1px solid #dce4ef;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.result-row > *:last-child {
  border-right: 0;
}

.result-head {
  background: #edf3fa;
  font-weight: 700;
}

.result-alert {
  color: #c23939;
}

.result-alert.danger-h {
  color: #ff0000;
}

.result-alert.danger-l {
  color: #0a7ffb;
}

.result-input--focused .el-input__inner, .result-input--changed .el-input__inner {
  background-color: #fff5dc !important;
}

.result-detail {
  min-height: 530px;
  padding: 36px;
  border: 1px solid #e1e7f0;
}

.back-button {
  margin-bottom: 28px;
  border: 0;
  background: transparent;
  padding: 0;
  color: #2d69d1;
  cursor: pointer;
  font-weight: 600;
}

.result-detail dl {
  margin: 28px 0;
  border-top: 1px solid #e4eaf2;
}

.result-detail dl div {
  display: grid;
  grid-template-columns: 150px 1fr;
  padding: 13px 0;
  border-bottom: 1px solid #e4eaf2;
}

.result-detail dt {
  color: #8794a7;
}

.result-detail dd {
  margin: 0;
  font-weight: 600;
}

.detail-note, .detail-files {
  margin-top: 14px;
  padding: 16px;
  background: #f4f8fd;
  border-radius: 8px;
  word-break: break-word;
}

.detail-note p {
  margin: 9px 0 0;
  line-height: 1.6;
}

.detail-note div {
  margin-top: 9px;
  line-height: 1.6;
}

.detail-files a {
  display: block;
  margin-top: 8px;
  color: #2864d7;
  overflow-wrap: anywhere;
}

.result-list h2 {
  margin-bottom: 22px;
}

.result-list > button {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border: 0;
  border-bottom: 1px solid #e6ebf2;
  background: transparent;
  color: #314663;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
}

.result-list > button span:last-child {
  display: flex;
  align-items: center;
  gap: 10px;
}

.result-list em {
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
}

.result-list > button:hover {
  color: #2767d5;
}

.detail-slide-enter-active, .detail-slide-leave-active {
  transition: all .22s ease;
}

.detail-slide-enter {
  opacity: 0;
  transform: translateX(30px);
}

.detail-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.result-edit-reason {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #dce4ef;
  border-radius: 8px;
  background: #f8fafc;
}

.result-edit-reason label {
  display: block;
  margin-bottom: 8px;
  color: #364d70;
  font-size: 13px;
  font-weight: 700;
}

.result-last-change {
  margin: 0 0 12px;
  padding: 9px 12px;
  border: 1px solid #dce4ef;
  border-radius: 7px;
  color: #56677f;
  background: #f8fafc;
  font-size: 12px;
}

.result-last-change i {
  color: #2864d7;
}

.result-history-drawer {
  position: absolute;
  z-index: 4;
  top: 0;
  right: 0;
  width: min(500px, 92%);
  height: 100%;
  padding: 18px;
  overflow-y: auto;
  border-left: 1px solid #dce4ef;
  background: #fff;
  box-shadow: -8px 0 24px rgba(23, 43, 77, .14);
}

.result-content {
  position: relative;
}

.result-history-drawer__header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.result-history-drawer__header h3 {
  margin: 0;
  color: #1b3b70;
  font-size: 20px;
}

.result-history-drawer__header button {
  border: 0;
  background: transparent;
  color: #64748b;
  font-size: 20px;
  cursor: pointer;
}

.result-history-select {
  width: 100%;
  margin-bottom: 12px;
}

.result-history-list button {
  width: 100%;
  margin-bottom: 8px;
  padding: 12px;
  border: 1px solid #e1e7f0;
  border-radius: 7px;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.result-history-list button:hover, .result-history-list button.active {
  border-color: #5b91ed;
  background: #f5f9ff;
}

.result-history-list button strong, .result-history-list button span, .result-history-list button small {
  display: block;
}

.result-history-list button span, .result-history-list button small {
  margin-top: 4px;
  color: #718096;
  font-size: 11px;
}

.result-history-detail {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #dce4ef;
}

.result-history-detail h4, .result-history-detail p {
  margin: 0 0 8px;
}

.result-history-detail p {
  color: #64748b;
  font-size: 12px;
}

.result-history-detail article {
  margin-top: 10px;
  padding: 11px;
  border-radius: 7px;
  background: #f8fafc;
}

.result-history-detail article > strong {
  display: block;
  color: #304765;
}

.result-history-detail article > small {
  color: #718096;
}

.result-history-detail article > div {
  display: grid;
  grid-template-columns: 1.1fr 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
}

.result-history-detail del {
  color: #a05050;
}

.result-history-detail b {
  color: #247650;
}

.history-drawer-enter-active, .history-drawer-leave-active {
  transition: transform .22s ease, opacity .22s ease;
}

.history-drawer-enter, .history-drawer-leave-to {
  opacity: 0;
  transform: translateX(32px);
}

@media (max-width: 1100px) {
  .overview-body {
    grid-template-columns: 190px minmax(280px, .8fr) minmax(380px, 1.2fr);
  }
  .preview-toolbar, .preview-content {
    padding-left: 24px;
    padding-right: 24px;
  }
}

@media print {
  body * {
    visibility: hidden;
  }
  .indication-overview-dialog, .indication-overview-dialog * {
    visibility: visible;
  }
  .indication-overview-dialog {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    margin: 0 !important;
    box-shadow: none !important;
  }
  .category-menu, .order-panel, .preview-toolbar, .el-dialog__header {
    display: none !important;
  }
  .overview-body, .preview-panel, .preview-content {
    display: block !important;
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    padding: 0 !important;
  }
  .indication-content-list, .result-content, .result-detail {
    border: 0 !important;
    max-width: none !important;
    min-height: 0 !important;
  }
}
</style>
